import { DuviNicheSiteButton } from "@/components/duvi-niche-site-button";

export interface DuviNicheSiteHeroCta {
  label: string;
  /** Cross-origin hand-off (business portal). Mutually exclusive with `to`. */
  href?: string;
  /** Same-site route. Mutually exclusive with `href`. */
  to?: string;
}

export interface DuviNicheSiteHeroProps {
  eyebrow: string;
  headline: string;
  subheading: string;
  primaryCta: DuviNicheSiteHeroCta;
  secondaryCta?: DuviNicheSiteHeroCta;
  /** Real Fishwalkers photography, drawn down from the design baseline. */
  mediaSrc: string;
  mediaAlt: string;
}

/**
 * DuviNicheSiteHero — ported from the bespoke Fishwalkers design system's
 * `components/marketing/Hero.jsx`
 * (duvi-design/exports/niches/fishwalkers/system.zip): eyebrow, display
 * headline, lead, a CTA pair, and the media panel alongside.
 *
 * The export renders a `MediaSlot`, which is a labelled placeholder until
 * real photography lands. It has landed — `IMG-01`,
 * `duvi-design/exports/niches/fishwalkers/imagery/hero.webp`, drawn down into
 * `src/assets/` — so this renders the slot's `src` branch (its exact classes
 * and 4/3 ratio) and does not carry the placeholder branch that can no longer
 * render. Intrinsic dimensions, eager loading and `fetchPriority` are kept
 * from the previous build: this image is the page's LCP element.
 */
export function DuviNicheSiteHero({
  eyebrow,
  headline,
  subheading,
  primaryCta,
  secondaryCta,
  mediaSrc,
  mediaAlt,
}: DuviNicheSiteHeroProps) {
  return (
    <section className="w-full bg-[var(--canvas)] py-[var(--section-y)] lg:py-[var(--section-y-lg)]">
      <div className="mx-auto grid w-full max-w-[var(--container-max)] grid-cols-1 items-center gap-[var(--space-12)] px-[var(--container-pad)] lg:grid-cols-2 lg:gap-[var(--grid-gap-lg)] lg:px-[var(--container-pad-lg)]">
        <div className="flex flex-col items-start gap-[var(--space-5)]">
          <span className="font-[family-name:var(--font-body)] text-[length:var(--text-eyebrow)] font-semibold uppercase tracking-[var(--tracking-eyebrow)] text-[color:var(--brand)]">
            {eyebrow}
          </span>
          <h1 className="font-[family-name:var(--font-display)] text-[length:var(--text-display-sm)] font-extrabold leading-[var(--leading-display)] tracking-[var(--tracking-display)] text-[color:var(--heading)] text-pretty lg:text-[length:var(--text-display)]">
            {headline}
          </h1>
          <p className="m-0 max-w-[var(--measure-lead)] font-[family-name:var(--font-body)] text-[length:var(--text-lead-sm)] leading-[var(--leading-lead)] text-[color:var(--body)] lg:text-[length:var(--text-lead)]">
            {subheading}
          </p>
          <div className="mt-[var(--space-2)] flex flex-wrap items-center gap-[var(--space-3)]">
            <DuviNicheSiteButton
              href={primaryCta.href}
              to={primaryCta.to}
              size="lg"
            >
              {primaryCta.label}
            </DuviNicheSiteButton>
            {secondaryCta && (
              <DuviNicheSiteButton
                href={secondaryCta.href}
                to={secondaryCta.to}
                size="lg"
                variant="secondary"
              >
                {secondaryCta.label}
              </DuviNicheSiteButton>
            )}
          </div>
        </div>
        <img
          src={mediaSrc}
          alt={mediaAlt}
          width={1440}
          height={1080}
          loading="eager"
          // React 18 does not know `fetchPriority` and warns if it is passed
          // camelCased; the attribute itself is lowercase in HTML. React 19
          // adds the typed prop — drop the spread then.
          {...{ fetchpriority: "high" }}
          className="block h-full w-full rounded-[var(--radius-card)] object-cover"
          style={{ aspectRatio: "4 / 3" }}
        />
      </div>
    </section>
  );
}
